/**
 * Regions Sagas
 * Async operations for regions domain
 */

import { call, put, takeLatest, all, fork, select } from '@spcc/core'
import type { PayloadAction } from '@spcc/core'

import { regionsActions } from './regions.slice'
import { selectRegionsFilter } from './regions.selectors'
import { regionsApi, factoriesForRegionsApi, countriesForRegionsApi } from '../infrastructure/api/regions-api'
import type {
  Region,
  RegionsFilter,
  CreateRegionRequest,
  UpdateRegionRequest,
  PaginatedRegions,
  RegionStats,
  FactorySummary,
  CountrySummary,
} from '../domain/types'

// ===========================================
// Fetch Regions Saga
// ===========================================

function* fetchRegionsSaga(action: PayloadAction<RegionsFilter | undefined>) {
  try {
    const filter = action.payload ?? (yield select(selectRegionsFilter))
    const result: PaginatedRegions = yield call(regionsApi.getRegionsPaginated, filter)
    yield put(
      regionsActions.fetchRegionsSuccess({
        data: result.data,
        pagination: result.pagination,
      })
    )
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch regions'
    yield put(regionsActions.fetchRegionsFailure(message))
  }
}

// ===========================================
// Fetch Region Detail Saga
// ===========================================

function* fetchRegionDetailSaga(action: PayloadAction<string>) {
  try {
    const region: Region | null = yield call(regionsApi.getRegion, action.payload)
    if (region) {
      yield put(regionsActions.fetchRegionDetailSuccess(region))
    } else {
      yield put(regionsActions.fetchRegionDetailFailure('Region not found'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch region detail'
    yield put(regionsActions.fetchRegionDetailFailure(message))
  }
}

// ===========================================
// Fetch Region Stats Saga
// ===========================================

function* fetchRegionStatsSaga() {
  try {
    const stats: RegionStats = yield call(regionsApi.getRegionStats)
    yield put(regionsActions.fetchRegionStatsSuccess(stats))
  } catch (error) {
    // Stats fetch failure is non-critical, just log
    console.error('Failed to fetch region stats:', error)
  }
}

// ===========================================
// Fetch Available Factories Saga
// ===========================================

function* fetchAvailableFactoriesSaga() {
  try {
    const factories: FactorySummary[] = yield call(factoriesForRegionsApi.getActivefactories)
    yield put(regionsActions.fetchAvailableFactoriesSuccess(factories))
  } catch (error) {
    console.error('Failed to fetch available factories:', error)
  }
}

// ===========================================
// Fetch Available Countries Saga
// ===========================================

function* fetchAvailableCountriesSaga() {
  try {
    const countries: CountrySummary[] = yield call(countriesForRegionsApi.getActiveCountries)
    yield put(regionsActions.fetchAvailableCountriesSuccess(countries))
  } catch (error) {
    console.error('Failed to fetch available countries:', error)
  }
}

// ===========================================
// Create Region Saga
// ===========================================

function* createRegionSaga(action: PayloadAction<CreateRegionRequest>) {
  try {
    const region: Region = yield call(regionsApi.createRegion, action.payload)
    yield put(regionsActions.createRegionSuccess(region))
    // Refresh stats after creation
    yield put(regionsActions.fetchRegionStats())
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to create region'
    yield put(regionsActions.createRegionFailure(message))
  }
}

// ===========================================
// Update Region Saga
// ===========================================

function* updateRegionSaga(
  action: PayloadAction<{ documentId: string; data: UpdateRegionRequest }>
) {
  try {
    const region: Region = yield call(
      regionsApi.updateRegion,
      action.payload.documentId,
      action.payload.data
    )
    yield put(regionsActions.updateRegionSuccess(region))
    // Refresh stats after update
    yield put(regionsActions.fetchRegionStats())
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update region'
    yield put(regionsActions.updateRegionFailure(message))
  }
}

// ===========================================
// Delete Region Saga
// ===========================================

function* deleteRegionSaga(action: PayloadAction<string>) {
  try {
    const success: boolean = yield call(regionsApi.deleteRegion, action.payload)
    if (success) {
      yield put(regionsActions.deleteRegionSuccess(action.payload))
      // Refresh stats after deletion
      yield put(regionsActions.fetchRegionStats())
    } else {
      yield put(regionsActions.deleteRegionFailure('Failed to delete region'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to delete region'
    yield put(regionsActions.deleteRegionFailure(message))
  }
}

// ===========================================
// Watcher
// ===========================================

function* watchRegions() {
  yield takeLatest(regionsActions.fetchRegions.type, fetchRegionsSaga)
  yield takeLatest(regionsActions.fetchRegionDetail.type, fetchRegionDetailSaga)
  yield takeLatest(regionsActions.fetchRegionStats.type, fetchRegionStatsSaga)
  yield takeLatest(regionsActions.fetchAvailableFactories.type, fetchAvailableFactoriesSaga)
  yield takeLatest(regionsActions.fetchAvailableCountries.type, fetchAvailableCountriesSaga)
  yield takeLatest(regionsActions.createRegion.type, createRegionSaga)
  yield takeLatest(regionsActions.updateRegion.type, updateRegionSaga)
  yield takeLatest(regionsActions.deleteRegion.type, deleteRegionSaga)
}

// ===========================================
// Root Saga
// ===========================================

export function* regionsSaga() {
  yield all([fork(watchRegions)])
}
